import React from 'react';
import { LayoutDashboard, Users, ClipboardList, MessageSquare } from 'lucide-react';

/**
 * Mobile bottom tab bar (Spec 7.3). Renders below `md:` only — the desktop Navbar
 * carries `hidden md:flex` and this carries `md:hidden`, so exactly one nav is live
 * at any width.
 *
 * Bottom bar (not a hamburger) because the app has 5 flat destinations and is used
 * as a glance-then-drill-in surface; thumb reach matters more than chrome savings.
 * `admin` stays in the profile sheet — it's rare and role-gated.
 *
 * Safe area: the fixed bar pads with env(safe-area-inset-bottom) so the iPhone home
 * indicator never overlaps the tap targets. Every target is >=44px (Apple HIG).
 */

export type MobileNavPage = 'dashboard' | 'agents' | 'all-orders' | 'chat';

interface MobileNavProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

const ITEMS: { id: MobileNavPage; label: string; Icon: React.ElementType }[] = [
  { id: 'dashboard', label: 'Desk', Icon: LayoutDashboard },
  { id: 'agents', label: 'Agents', Icon: Users },
  { id: 'all-orders', label: 'Orders', Icon: ClipboardList },
  { id: 'chat', label: 'Forum', Icon: MessageSquare },
];

const MobileNav: React.FC<MobileNavProps> = ({ currentPage, onNavigate }) => (
  <nav
    aria-label="Primary"
    data-testid="mobile-nav"
    className="md:hidden fixed bottom-0 inset-x-0 z-50 flex items-stretch
               border-t border-slate-700/60 bg-slate-900/95 backdrop-blur
               pb-[env(safe-area-inset-bottom)]"
  >
    {ITEMS.map(({ id, label, Icon }) => {
      const active = currentPage === id;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onNavigate(id)}
          aria-current={active ? 'page' : undefined}
          // min-h-[44px] + flex-1 keeps every target >= the 44px HIG minimum
          className={`flex-1 min-h-[44px] flex flex-col items-center justify-center gap-0.5 py-2
                      touch-manipulation transition-colors ${
                        active ? 'text-cyan-400' : 'text-slate-400'
                      }`}
        >
          <Icon className="w-5 h-5" aria-hidden="true" />
          <span className="text-10 font-medium leading-none">{label}</span>
        </button>
      );
    })}
  </nav>
);

export default MobileNav;
